
import { defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { companies } from '@/core/realisations/companies.realisation'

// constants
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'

// validations
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-select': TheSelect,
        'the-button': TheButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['reset', 'search'],
    setup(props, { emit }) {
        const state = reactive({
            carrier_id: null as number | null,
            driver_name: null as string | null,
            driver_phone: null as string | null,
            fromDate: null as string | null,
            toDate: null as string | null,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const GetRPPValidation = {
            carrier_id: { required },
        }

        const state_form$ = useVuelidate(GetRPPValidation, state)

        const reset = () => {
            state.carrier_id = null
            state.driver_name = null
            state.driver_phone = null
            state.fromDate = null
            state.toDate = null

            emit('reset')
        }

        const search = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('search', {
                carrier_id: state.carrier_id,
                driver_name: state.driver_name,
                driver_phone: state.driver_phone,
                fromDate: state.fromDate,
                toDate: state.toDate,
            })
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({ page, search, statusId: 1 })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 1 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        onBeforeMount(() => {
            loadCompanies(1)
        })

        return {
            state,
            data_state,
            state_form$,
            // companies
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            // form
            reset,
            search,
            // constants
            PHONE_NUMBER_MASK,
        }
    },
})
